import { defineStore } from "pinia";
import { getIanaFromRailsZone } from "~/utils/time-zones";
import type Org from "~/types/OrgType";
import { useMediaListStore } from "~/store/mediaList";
import locations from "~/constants/locations";
import type { Feature } from "~/types/FeatureTypes";
import { useFeatureManagement } from "~/composables/useFeatureManagement";
import { useApiCall } from "~/composables/useApiCall.ts";
import { EXTERNAL_LINKS } from "~/constants/urls.ts";
import { useAlertStore } from "~/store/alert.ts";

type FmsMediaType = "digital" | "print" | "broadcast";

type State = {
  org: Org | null;
};

interface PDFViewResponse {
  data: {
    value: {
      redirect_url: string;
    };
  };
}

export const useOrgStore = defineStore("org", {
  state: (): State => ({
    org: null,
  }),
  getters: {
    getOrg(state): Org | null {
      return state.org;
    },
    getFeatures(state): Feature[] | null {
      return state.org?.attributes?.features || null;
    },
    getWordPlay() {
      const { isFeatureEnabled } = useFeatureManagement();
      return isFeatureEnabled("wordplay");
    },
    getTimeZone(state) {
      return getIanaFromRailsZone(state.org?.attributes.time_zone);
    },
    getTMLLimit() {
      const { getFeatureConfig } = useFeatureManagement();
      return getFeatureConfig<number>("targetMediaLists.maxLists");
    },
    getTMLDomainsLimit() {
      const { getFeatureConfig } = useFeatureManagement();
      return getFeatureConfig<number>("targetMediaLists.maxDomainsPerList");
    },
    getAllowedCountries() {
      const { getAllowedCountries } = useFeatureManagement();
      const allowedCountryISOs = getAllowedCountries();

      // If empty array, it means global access - show all countries
      if (allowedCountryISOs.length === 0) {
        return locations.ALL_SORTED_DROPDOWN;
      }

      // Filter countries to only show those in the allowed list
      return locations.ALL_SORTED_DROPDOWN.filter((location) =>
        allowedCountryISOs.includes(location.iso),
      );
    },
    hasPrintMediaAccess() {
      const { isFeatureEnabled, getFeatureConfig } = useFeatureManagement();

      // Check if licensed print is enabled AND has actual licenses
      const hasLicensedAccess =
        isFeatureEnabled("printMediaLicensed") &&
        (getFeatureConfig<string[]>("printMediaLicensed.printLicenses") || [])
          .length > 0;

      // Check if unlicensed print is enabled (existence implies access)
      const hasUnlicensedAccess = isFeatureEnabled("printMediaUnlicensed");

      return hasLicensedAccess || hasUnlicensedAccess;
    },
    hasBroadcastMediaAccess() {
      const { isFeatureEnabled, getFeatureConfig } = useFeatureManagement();

      // Check if licensed broadcast is enabled AND has actual licenses
      const hasLicensedAccess =
        isFeatureEnabled("broadcastMediaLicensed") &&
        (
          getFeatureConfig<string[]>(
            "broadcastMediaLicensed.broadcastLicenses",
          ) || []
        ).length > 0;

      // Check if unlicensed broadcast is enabled (existence implies access)
      const hasUnlicensedAccess = isFeatureEnabled("broadcastMediaUnlicensed");

      return hasLicensedAccess || hasUnlicensedAccess;
    },
    hasPodcastAccess() {
      const { isFeatureEnabled } = useFeatureManagement();
      return isFeatureEnabled("podcastMedia");
    },
    hasSocialMediaAccess() {
      const { isFeatureEnabled } = useFeatureManagement();

      // Access granted if social media feature is enabled
      return isFeatureEnabled("socialMedia");
    },
    hasSocialPlatformAccess(): (platform: string) => boolean {
      const { isSocialPlatformEnabled } = useFeatureManagement();

      return (platform: string) => isSocialPlatformEnabled(platform);
    },
    hasArticleSentiment() {
      const { isFeatureEnabled } = useFeatureManagement();
      return isFeatureEnabled("articleSentiment");
    },
    hasPDFAccess() {
      const { isFeatureEnabled } = useFeatureManagement();
      return isFeatureEnabled("pdfAccess");
    },
    hasDigitalMediaAccess() {
      const { isFeatureEnabled, getFeatureConfig } = useFeatureManagement();

      // Check if licensed digital is enabled AND has actual licenses
      const hasLicensedAccess =
        isFeatureEnabled("digitalMediaLicensed") &&
        (
          getFeatureConfig<string[]>("digitalMediaLicensed.digitalLicenses") ||
          []
        ).length > 0;

      // Check if unlicensed digital is enabled (existence implies access)
      const hasUnlicensedAccess = isFeatureEnabled("digitalMediaUnlicensed");

      return hasLicensedAccess || hasUnlicensedAccess;
    },
    hasAnyMediaAccess(): boolean {
      return (
        this.hasDigitalMediaAccess ||
        this.hasPrintMediaAccess ||
        this.hasBroadcastMediaAccess ||
        this.hasPodcastAccess ||
        this.hasSocialMediaAccess
      );
    },
    canAccessMediaType() {
      const { canAccessMedia } = useFeatureManagement();
      return (mediaType: string, region?: string, license?: string) => {
        return canAccessMedia(mediaType as FmsMediaType, region, license);
      };
    },
    getMediaLimits() {
      const { getFeatureConfig, getLockingBehavior } = useFeatureManagement();
      return (mediaType: string) => {
        const licensedSlug = `${mediaType}MediaLicensed`;
        const unlicensedSlug = `${mediaType}MediaUnlicensed`;

        const lockingBehavior = getLockingBehavior(mediaType as FmsMediaType);

        return {
          licensed: {
            overallLimit: getFeatureConfig<number>(
              `${licensedSlug}.accessLimit`,
            ),
            automatedLimit: getFeatureConfig<number>(
              `${licensedSlug}.automatedLimit`,
            ),
            lockingBehavior,
            geographicLocations:
              getFeatureConfig<string[]>(
                `${licensedSlug}.geographicLocations`,
              ) || [],
            licenses:
              getFeatureConfig<string[]>(
                `${licensedSlug}.${mediaType}Licenses`,
              ) || [],
          },
          unlicensed: {
            overallLimit: getFeatureConfig<number>(
              `${unlicensedSlug}.accessLimit`,
            ),
            automatedLimit: getFeatureConfig<number>(
              `${unlicensedSlug}.automatedLimit`,
            ),
            lockingBehavior,
            geographicLocations:
              getFeatureConfig<string[]>(
                `${unlicensedSlug}.geographicLocations`,
              ) || [],
          },
        };
      };
    },
    isTrialAccount(state) {
      return state.org?.attributes.trial_account;
    },
    hasSocialTracking() {
      // eslint-disable-next-line no-console
      console.warn(
        "[DEPRECATED] hasSocialTracking() - social-media feature is deprecated",
      );
      return false;
    },
    hasExceededTMLLimit() {
      const mediaListStore = useMediaListStore();
      const { getFeatureConfig } = useFeatureManagement();

      const maxLists = getFeatureConfig<number>("targetMediaLists.maxLists");
      return maxLists ? mediaListStore.mediaLists.length >= maxLists : false;
    },
    hasExceededEmailAlertsLimit(): boolean {
      const alertsStore = useAlertStore();
      const { getFeatureConfig } = useFeatureManagement();

      const maxEmailAlerts = getFeatureConfig<number>("emailAlerts.maxAlerts");

      return maxEmailAlerts
        ? alertsStore.totalRealTimeAlertsCount >= maxEmailAlerts
        : false;
    },

    hasExceededDigestAlertsLimit(): boolean {
      const alertsStore = useAlertStore();
      const { getFeatureConfig } = useFeatureManagement();

      const maxEmailDigests = getFeatureConfig<number>(
        "emailDigests.maxDigests",
      );

      return maxEmailDigests
        ? alertsStore.totalDigestAlertsCount >= maxEmailDigests
        : false;
    },

    hasUKLicense(): boolean {
      const { getPrintLicenses } = useFeatureManagement();
      const printLicenses = getPrintLicenses();
      return !!printLicenses.filter((license) =>
        ["NLA", "NLI", "CLA"].includes(license),
      ).length;
    },
    hasTwitterAmp(): boolean {
      // eslint-disable-next-line no-console
      console.warn(
        "[DEPRECATED] hasTwitterAmp() - twitter-amplification feature is deprecated",
      );
      return false;
    },
    // Navigation permission getters - FMS-based
    canAccessTargetMediaLists() {
      const { isFeatureEnabled } = useFeatureManagement();
      return isFeatureEnabled("targetMediaLists");
    },
    canAccessAlerts() {
      const { isFeatureEnabled } = useFeatureManagement();
      return isFeatureEnabled("emailAlerts");
    },
    canAccessReports() {
      // Add specific report feature check if needed, otherwise default to true
      return true;
    },
    canAccessPrm() {
      const { isFeatureEnabled } = useFeatureManagement();
      const hasFeatureAccess = isFeatureEnabled("prmAccess");
      return hasFeatureAccess;
    },
    canAccessUnlockedMentions(state): boolean {
      return state.org?.attributes?.show_manage_unlocked_articles_page ?? false;
    },
  },
  actions: {
    setOrg(org: Org) {
      this.org = org;
    },
    async fetchPdfUrl(
      articleId: string,
      articleCountry: string,
      mediaType?: string,
      licenses?: string[],
      truncate?: number,
    ) {
      const params = new URLSearchParams();
      if (articleCountry) params.set("country", articleCountry);
      if (mediaType) params.set("media_type", mediaType);
      licenses?.forEach((l) => params.append("licenses[]", l));
      if (truncate !== undefined && truncate > 0)
        params.set("truncate_amount", String(truncate));
      const pdfView: PDFViewResponse = await useApiCall(
        `/article_searches/${articleId}/pdf_view?${params.toString()}`,
        { method: "GET" },
      );
      const redirectUrl: string = pdfView?.data.value.redirect_url ?? "";
      return redirectUrl;
    },

    async fetchPdfBlob(
      articleId: string,
      articleCountry: string,
      mediaType?: string,
      licenses?: string[],
      truncate?: number,
    ) {
      const params = new URLSearchParams();
      if (articleCountry) params.set("country", articleCountry);
      if (mediaType) params.set("media_type", mediaType);
      licenses?.forEach((l) => params.append("licenses[]", l));
      if (truncate !== undefined && truncate > 0)
        params.set("truncate_amount", String(truncate));
      const response = await useApiCall(
        `/article_searches/${articleId}/pdf?${params.toString()}`,
        { method: "GET", responseType: "blob" },
        undefined,
        "rails",
        false,
        true,
      );
      return response?.data?.value as Blob | null;
    },

    async fetchNlaPdfUrl(
      articleId: string,
      articleCountry: string,
      truncate?: number,
    ) {
      const pdfUrl = await this.fetchPdfUrl(
        articleId,
        articleCountry,
        undefined,
        undefined,
        truncate,
      );
      const nlaUrl = EXTERNAL_LINKS.NLA_ECLIPS_URL;
      const cleanedMentionId = String(articleId).replace(/^nla-/, "");
      return `${nlaUrl}&ObjectID=${cleanedMentionId}&PCAURL=${pdfUrl}`;
    },
  },
});
